import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { FaExclamationTriangle } from "react-icons/fa";

const DocSearch = () => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(0);
  const { currentUser } = useSelector(state => state.auth);
  const email = currentUser?.email;
  const docSearchAppUrl = process.env.REACT_APP_DOC_SEARCH_APP_URL;

  // Only restore the last visited path (pathname only, no query string)
  // to avoid conflicts when the doc-search app appends its own search params
  const targetPath = useMemo(() => {
    const stored = localStorage.getItem("doc_search_app_path") || "/";
    // Strip query string — only keep pathname
    return stored.split("?")[0] || "/";
  }, []);

  const { fontFamily, fontSize } = useMemo(() => {
    const computed = window.getComputedStyle(document.body);
    return {
      fontFamily: computed.fontFamily,
      fontSize: computed.fontSize,
    };
  }, []);

  // iframe src has NO Portal params — clean URL so the doc-search app
  // can freely append ?q=... during search without URL conflicts
  const fullIframeUrl = useMemo(
    () => email && docSearchAppUrl
      ? `${docSearchAppUrl}${targetPath}`
      : "",
    [email, docSearchAppUrl, targetPath]
  );

  useEffect(() => {
    const calculateHeight = () => {
      const navbar =
        document.querySelector(".ptl-navbar-top") ||
        document.querySelector("nav.navbar") ||
        document.querySelector('[class*="navbar-top"]');
      const footer =
        document.querySelector("footer") ||
        document.querySelector('[class*="footer"]');

      const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0;

      const available = window.innerHeight - navbarHeight - footerHeight - 20;
      setIframeHeight(available);
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  // Send config to iframe — called both on onLoad and when iframe signals READY
  const sendConfig = useCallback(() => {
    if (iframeRef.current?.contentWindow && email && docSearchAppUrl) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: "SET_CRM_CONFIG",
          email,
          fontFamily,
          fontSize,
          isOpenSidebar: false,
        },
        new URL(docSearchAppUrl).origin
      );
    }
  }, [email, fontFamily, fontSize, docSearchAppUrl]);

  useEffect(() => {
    const handleMessage = event => {
      if (!docSearchAppUrl) return;
      try {
        if (event.origin !== new URL(docSearchAppUrl).origin) return;
      } catch (_) { return; }

      const { type, path } = event.data || {};

      if (type === "SYNC_PATH" && path) {
        // Only store pathname — strip search query to avoid stale params
        localStorage.setItem("doc_search_app_path", path.split("?")[0] || "/");
      }

      // DOC_SEARCH_READY: CrmTokenReceiver in iframe has mounted its listener — safe to send config now
      if (type === "DOC_SEARCH_READY") {
        sendConfig();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [docSearchAppUrl, sendConfig]);

  // onLoad: first attempt (may arrive before useEffect in iframe registers listener)
  const handleIframeLoad = () => {
    sendConfig();
  };

  if (!email) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-items-center tw-justify-center tw-bg-slate-50"
      >
        <div className="tw-text-center tw-p-5">
          <h4 className="tw-text-slate-500 tw-mb-3">Authentication Required</h4>
          <p className="tw-text-slate-500">
            Please login to Portal to access the Doc Search system.
          </p>
        </div>
      </div>
    );
  }

  if (!docSearchAppUrl) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-4"
      >
        <div className="tw-text-red-600 tw-mb-3">
          <FaExclamationTriangle style={{ fontSize: "3rem" }} />
        </div>
        <h4 className="tw-font-bold">Doc Search URL not configured</h4>
        <p className="tw-text-slate-500">
          Please set <code>REACT_APP_DOC_SEARCH_APP_URL</code> in environment config.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="tw-p-1 tw-bg-white tw-rounded-sm"
      style={{ width: "100%", height: iframeHeight || "80vh" }}
    >
      <iframe
        ref={iframeRef}
        src={fullIframeUrl}
        title="Doc Search App"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block"
        }}
        allowFullScreen
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

export default DocSearch;
